import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from './config';
import { copyStoredRoute } from './sourceRoutes';
import {
  VendorListParams,
  VendorListResponse,
  VendorDetailResponse,
  VendorItem,
  SourceRoute,
  VendorCreateRequest,
  SmbEndpointOption,
  VendorReinviteRequest,
  VendorMutationResponse,
  VendorUpdateRequest,
  VendorUpdateResponse,
  VendorArchiveResponse,
  BusinessUnitSetting,
  AdHocTransferRequest,
  AdHocTransferResponse,
  AdHocAcceptedFile,
  AdHocRejectedFile,
  TransferListParams,
  TransferListResponse,
  TransferDetailResponse,
  TransferDetailResult,
  TransferHistoryEntry,
  TransferRetryResponse,
} from '@/types';

class VendorApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.vendorApiGatewayUrl,
      timeout: 30000,
    });

    this.api.interceptors.request.use(
      async (req: InternalAxiosRequestConfig) => {
        if (!config.vendorApiGatewayUrl) {
          return Promise.reject(
            new Error(
              'VITE_VENDOR_API_GATEWAY_URL is not set. Add it to local .env (dev) or the Bitbucket build variables (deployed builds).'
            )
          );
        }
        try {
          let session = await fetchAuthSession({ forceRefresh: false });
          if (!session.tokens) {
            session = await fetchAuthSession({ forceRefresh: true });
          }
          if (!session.tokens) {
            return Promise.reject(new Error('No authentication tokens available. Please log in.'));
          }
          let token = session.tokens.idToken?.toString();
          if (!token) {
            token = session.tokens.accessToken?.toString();
          }
          if (!token) {
            return Promise.reject(new Error('No authentication tokens available. Please log in.'));
          }
          req.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          return Promise.reject(error);
        }
        return req;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const status = error.response?.status;
        if (status === 401) {
          try {
            await fetchAuthSession({ forceRefresh: true });
            return this.api.request(error.config as InternalAxiosRequestConfig);
          } catch {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async listVendors(params: VendorListParams = {}): Promise<VendorListResponse> {
    const response = await this.api.get('/admin/vendors/api', {
      params: {
        vendorId: params.vendorId,
        sftpUsername: params.sftpUsername,
        status: params.status,
        includeArchived: params.includeArchived ? true : undefined,
        tag: params.tag,
        limit: params.limit,
        nextToken: params.nextToken,
      },
    });
    const data = response.data || {};
    return {
      vendors: Array.isArray(data.vendors) ? data.vendors : [],
      count: typeof data.count === 'number' ? data.count : 0,
      nextToken: data.nextToken,
    };
  }

  async listOperatorVendors(): Promise<VendorItem[]> {
    const OPERATOR_LIST_STATUS = 'completed';
    const OPERATOR_LIST_PAGE_LIMIT = 100;
    const OPERATOR_LIST_MAX_PAGES = 10;
    const OPERATOR_LIST_MAX_ITEMS = 500;

    const collected: VendorItem[] = [];
    let nextToken: string | undefined;
    let pages = 0;

    for (;;) {
      if (pages >= OPERATOR_LIST_MAX_PAGES || collected.length >= OPERATOR_LIST_MAX_ITEMS) {
        console.warn(
          `listOperatorVendors: pagination cap reached (${pages} pages, ${collected.length} items); remaining pages omitted`
        );
        break;
      }

      const response = await this.api.get('/vendors', {
        params: {
          status: OPERATOR_LIST_STATUS,
          limit: OPERATOR_LIST_PAGE_LIMIT,
          ...(nextToken ? { nextToken } : {}),
        },
      });
      const data = response.data || {};
      const page: VendorItem[] = Array.isArray(data.vendors) ? data.vendors : [];
      collected.push(...page);
      pages += 1;

      const token =
        (typeof data.nextToken === 'string' && data.nextToken) ||
        (typeof data.next_token === 'string' && data.next_token) ||
        '';
      if (!token) {
        break;
      }
      nextToken = token;
    }

    return collected.filter((vendor) => vendor.hostname === 'default');
  }

  async listOperatorVendorsPage(params: VendorListParams = {}): Promise<VendorListResponse> {
    const response = await this.api.get('/vendors', {
      params: {
        vendorId: params.vendorId,
        sftpUsername: params.sftpUsername,
        status: params.status,
        includeArchived: params.includeArchived ? true : undefined,
        tag: params.tag,
        limit: params.limit,
        nextToken: params.nextToken,
      },
    });
    const data = response.data || {};
    return {
      vendors: Array.isArray(data.vendors) ? data.vendors : [],
      count: typeof data.count === 'number' ? data.count : 0,
      nextToken: data.nextToken,
    };
  }

  async getVendor(vendorId: string, hostname = 'default'): Promise<VendorItem> {
    try {
      const response = await this.api.get<VendorDetailResponse>('/admin/vendors/api', {
        params: { vendorId, hostname },
      });
      const vendor = response.data?.vendor;
      if (!vendor) {
        throw new VendorNotFoundError(vendorId, hostname);
      }
      return vendor;
    } catch (error) {
      if (error instanceof VendorNotFoundError) {
        throw error;
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new VendorNotFoundError(vendorId, hostname);
      }
      throw error;
    }
  }

  async getOperatorVendor(vendorId: string, hostname = 'default'): Promise<VendorItem> {
    try {
      const response = await this.api.get<VendorDetailResponse>('/vendors', {
        params: { vendorId, hostname },
      });
      const vendor = response.data?.vendor;
      if (!vendor) {
        throw new VendorNotFoundError(vendorId, hostname);
      }
      return vendor;
    } catch (error) {
      if (error instanceof VendorNotFoundError) {
        throw error;
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new VendorNotFoundError(vendorId, hostname);
      }
      throw error;
    }
  }

  // GET /transfers — one BU-scoped page. The caller pages with nextToken.
  async listTransfers(params: TransferListParams = {}): Promise<TransferListResponse> {
    const response = await this.api.get('/transfers', {
      params: {
        status: params.status,
        triggeredBy: params.triggeredBy,
        vendorId: params.vendorId,
        tag: params.tag,
        limit: params.limit,
        nextToken: params.nextToken,
      },
    });
    const data = response.data || {};
    const nextToken =
      (typeof data.nextToken === 'string' && data.nextToken) ||
      (typeof data.next_token === 'string' && data.next_token) ||
      undefined;
    return {
      transfers: Array.isArray(data.transfers) ? data.transfers : [],
      count: typeof data.count === 'number' ? data.count : 0,
      nextToken,
    };
  }

  // GET /transfers?transferId= — one transfer plus its attempt history.
  // 404 becomes TransferNotFoundError. Callers that only need the row read `.transfer`.
  async getTransfer(transferId: string): Promise<TransferDetailResult> {
    try {
      const response = await this.api.get<TransferDetailResponse>('/transfers', {
        params: { transferId },
      });
      const transfer = response.data?.transfer;
      if (!transfer) {
        throw new TransferNotFoundError(transferId);
      }
      return {
        transfer,
        history: readHistory(response.data?.history),
      };
    } catch (error) {
      if (error instanceof TransferNotFoundError) {
        throw error;
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new TransferNotFoundError(transferId);
      }
      throw error;
    }
  }

  async retryTransfer(transferId: string): Promise<TransferRetryResponse> {
    try {
      const response = await this.api.post('/admin/transfers/retry', {
        transfer_id: transferId,
      });
      return readTransferRetry(response.data);
    } catch (error) {
      if (error instanceof TransferRetryError) {
        throw error;
      }
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (error.response?.data) {
          const parsed = readApiError(
            error.response.data,
            'The retry could not be started.',
          );
          throw new TransferRetryError(parsed.message, parsed.errorCode, status);
        }
        throw new TransferRetryError('The retry could not be started.', undefined, status);
      }
      throw new TransferRetryError('The retry could not be started.');
    }
  }

  async listEndpoints(): Promise<SmbEndpointOption[]> {
    const response = await this.api.get('/admin/endpoints');
    const data = response.data;
    if (!Array.isArray(data)) {
      throw new Error('Endpoint list was not a list');
    }
    return data.flatMap((row: unknown) => {
      if (!row || typeof row !== 'object') {
        return [];
      }
      const record = row as Record<string, unknown>;
      const endpointId = typeof record.endpoint_id === 'string' ? record.endpoint_id : '';
      const displayName = typeof record.display_name === 'string' ? record.display_name : '';
      if (!endpointId) {
        return [];
      }
      return [{ endpoint_id: endpointId, display_name: displayName }];
    });
  }

  async createVendor(body: VendorCreateRequest): Promise<VendorMutationResponse> {
    return this.postAdminVendor(body);
  }

  async reinviteVendor(vendor: VendorItem): Promise<VendorMutationResponse> {
    // Regenerate keeps stored source fields. Do not send source_routes or the
    // flat source_endpoint_id / source_folder / file_pattern.
    const body: VendorReinviteRequest = {
      vendor_id: vendor.vendorId,
      hostname: vendor.hostname || 'default',
      bu: (vendor.bu || '').trim(),
      sftp_username: vendor.sftpUsername,
      vendor_email: vendor.vendorEmail,
      success_email: vendor.successEmail,
      failure_email: vendor.failureEmail,
      outbound_bucket: vendor.outboundBucket,
      send_email_to_vendor: true,
      regenerate: true,
    };
    const prefix = vendor.outboundPrefix?.trim();
    if (prefix) {
      body.outbound_prefix = prefix;
    }
    return this.postAdminVendor(body);
  }

  async triggerAdHocTransfer(body: AdHocTransferRequest): Promise<AdHocTransferResponse> {
    const vendorId = (body.vendor_id || '').trim();
    const payload: AdHocTransferRequest = {
      vendor_id: vendorId,
      pattern: body.pattern || 'fsx_encrypt_and_move',
      files: (body.files || []).map((f) => String(f).trim()).filter(Boolean),
    };
    const resubmitOf = body.resubmit_of?.trim();
    if (resubmitOf) {
      payload.resubmit_of = resubmitOf;
    }

    try {
      const response = await this.api.post<AdHocTransferResponse>(
        '/admin/transfers/ad-hoc',
        payload
      );
      return normalizeAdHocResponse(response.data, vendorId, payload.pattern);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return normalizeAdHocResponse(error.response.data, vendorId, payload.pattern);
      }
      throw error;
    }
  }

  async archiveVendor(vendorId: string, hostname: string): Promise<VendorArchiveResponse> {
    return this.postVendorLifecycle(vendorId, hostname, 'archive');
  }

  async restoreVendor(vendorId: string, hostname: string): Promise<VendorArchiveResponse> {
    return this.postVendorLifecycle(vendorId, hostname, 'restore');
  }

  async patchVendor(
    vendorId: string,
    hostname: string,
    body: VendorUpdateRequest,
  ): Promise<VendorUpdateResponse> {
    try {
      const response = await this.api.patch('/vendors', body, {
        params: { vendorId, hostname },
      });
      return readVendorUpdate(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw vendorUpdateError(error.response.data);
      }
      throw error;
    }
  }

  private async postVendorLifecycle(
    vendorId: string,
    hostname: string,
    action: 'archive' | 'restore',
  ): Promise<VendorArchiveResponse> {
    try {
      const response = await this.api.post(
        `/admin/vendors/${encodeURIComponent(vendorId)}/${action}`,
        undefined,
        { params: { hostname } },
      );
      return readVendorArchive(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        throw vendorUpdateError(error.response.data);
      }
      throw error;
    }
  }

  private async postAdminVendor(
    body: VendorCreateRequest | VendorReinviteRequest
  ): Promise<VendorMutationResponse> {
    try {
      const response = await this.api.post<VendorMutationResponse>('/admin/vendor', body);
      return normalizeMutation(response.data, body.vendor_id);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return normalizeMutation(error.response.data, body.vendor_id);
      }
      throw error;
    }
  }

  async listBuSettings(): Promise<BusinessUnitSetting[]> {
    try {
      const response = await this.api.get('/admin/bu-settings');
      const settings = response.data?.settings;
      if (!Array.isArray(settings)) {
        throw new BuSettingsError('Business unit settings were not a list');
      }
      return settings.flatMap((row: unknown) => {
        const parsed = readBuSetting(row);
        return parsed ? [parsed] : [];
      });
    } catch (error) {
      throw buSettingsError(error, 'Failed to load business unit settings');
    }
  }

  async listTags(bu: string): Promise<string[]> {
    try {
      const response = await this.api.get(
        `/admin/bu-settings/${encodeURIComponent(bu)}/tags`,
      );
      const tags = response.data?.tags;
      if (!Array.isArray(tags)) {
        throw new BuSettingsError('Tag list was not a list');
      }
      return tags.filter((tag: unknown): tag is string => typeof tag === 'string' && tag.trim() !== '');
    } catch (error) {
      throw buSettingsError(error, 'Failed to load tags');
    }
  }

  async addTag(bu: string, tag: string): Promise<string[]> {
    try {
      const response = await this.api.post(
        `/admin/bu-settings/${encodeURIComponent(bu)}/tags`,
        { tag },
      );
      const tags = response.data?.tags;
      if (!Array.isArray(tags)) {
        throw new BuSettingsError('Tag list was not a list');
      }
      return tags.filter((entry: unknown): entry is string => typeof entry === 'string');
    } catch (error) {
      throw buSettingsError(error, 'Failed to add tag');
    }
  }

  async deleteTag(bu: string, tag: string): Promise<string[]> {
    try {
      const response = await this.api.delete(
        `/admin/bu-settings/${encodeURIComponent(bu)}/tags/${encodeURIComponent(tag)}`,
      );
      const tags = response.data?.tags;
      if (!Array.isArray(tags)) {
        throw new BuSettingsError('Tag list was not a list');
      }
      return tags.filter((entry: unknown): entry is string => typeof entry === 'string');
    } catch (error) {
      throw buSettingsError(error, 'Failed to remove tag');
    }
  }

  async updateBuSetting(bu: string, adminFallbackApprover: boolean): Promise<BusinessUnitSetting> {
    try {
      const response = await this.api.put(
        `/admin/bu-settings/${encodeURIComponent(bu)}`,
        { adminFallbackApprover },
      );
      const parsed = readBuSetting(response.data);
      if (!parsed) {
        throw new BuSettingsError('Business unit setting response was malformed');
      }
      return parsed;
    } catch (error) {
      throw buSettingsError(error, 'Failed to update business unit settings');
    }
  }
}

export class BuSettingsError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'BuSettingsError';
  }
}

function readBuSetting(data: unknown): BusinessUnitSetting | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const row = data as Record<string, unknown>;
  if (typeof row.bu !== 'string' || !row.bu.trim()) {
    return null;
  }
  return {
    bu: row.bu,
    adminFallbackApprover: row.adminFallbackApprover === true,
    updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
    updatedBy: typeof row.updatedBy === 'string' ? row.updatedBy : null,
  };
}

function buSettingsError(error: unknown, fallback: string): BuSettingsError {
  if (error instanceof BuSettingsError) {
    return error;
  }
  if (axios.isAxiosError(error)) {
    const parsed = error.response?.data
      ? readApiError(error.response.data, fallback)
      : { message: fallback };
    return new BuSettingsError(parsed.message, error.response?.status);
  }
  if (error instanceof Error && error.message) {
    return new BuSettingsError(error.message);
  }
  return new BuSettingsError(fallback);
}

export class VendorUpdateError extends Error {
  constructor(
    message: string,
    readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'VendorUpdateError';
  }
}

function readSourceRoutes(value: unknown): SourceRoute[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(copyStoredRoute);
}

function readVendorUpdate(data: unknown): VendorUpdateResponse {
  const raw = data && typeof data === 'object' ? data as Record<string, unknown> : {};
  const vendor = raw.vendor && typeof raw.vendor === 'object'
    ? raw.vendor as Record<string, unknown>
    : null;
  if (!vendor || typeof vendor.vendorId !== 'string' || typeof vendor.hostname !== 'string') {
    throw new VendorUpdateError('Update response was missing the vendor.');
  }
  const updated: VendorUpdateResponse = {
    vendorId: vendor.vendorId,
    hostname: vendor.hostname,
    vendor_email: typeof vendor.vendor_email === 'string' ? vendor.vendor_email : '',
    success_email: typeof vendor.success_email === 'string' ? vendor.success_email : '',
    failure_email: typeof vendor.failure_email === 'string' ? vendor.failure_email : '',
    source_endpoint_id: typeof vendor.source_endpoint_id === 'string' ? vendor.source_endpoint_id : '',
    source_folder: typeof vendor.source_folder === 'string' ? vendor.source_folder : '',
    file_pattern: typeof vendor.file_pattern === 'string' ? vendor.file_pattern : '',
    updatedAt: typeof vendor.updatedAt === 'string' ? vendor.updatedAt : '',
  };
  if (Array.isArray(vendor.source_routes)) {
    updated.source_routes = readSourceRoutes(vendor.source_routes);
  }
  if (typeof vendor.active === 'boolean') {
    updated.active = vendor.active;
  }
  return updated;
}

function readVendorArchive(data: unknown): VendorArchiveResponse {
  const raw = data && typeof data === 'object' ? data as Record<string, unknown> : {};
  const vendor = raw.vendor && typeof raw.vendor === 'object'
    ? raw.vendor as Record<string, unknown>
    : raw;
  const lifecycleStatus = vendor.lifecycleStatus === 'archived' ? 'archived' : vendor.lifecycleStatus;
  if (
    typeof vendor.vendorId !== 'string'
    || typeof vendor.hostname !== 'string'
    || (lifecycleStatus !== 'active' && lifecycleStatus !== 'archived')
  ) {
    throw new VendorUpdateError('Archive response was missing the vendor.');
  }
  return {
    vendorId: vendor.vendorId,
    hostname: vendor.hostname,
    lifecycleStatus,
    updatedAt: typeof vendor.updatedAt === 'string' ? vendor.updatedAt : '',
  };
}

export function readApiError(
  data: unknown,
  fallback: string,
): { errorCode?: string; message: string } {
  const raw = data && typeof data === 'object' ? data as Record<string, unknown> : {};
  const errorCode = typeof raw.error_code === 'string' ? raw.error_code : undefined;
  const message = typeof raw.message === 'string'
    ? raw.message
    : typeof raw.error === 'string'
      ? raw.error
      : fallback;
  return { errorCode, message };
}

function friendlyRouteEmailMessage(message: string): string {
  const labels: Record<string, string> = {
    success_email: 'Success emails',
    failure_email: 'Failure emails',
  };
  return message
    .replace(
      /source_routes\[(\d+)\]\s+(success_email|failure_email)(?:\[(\d+)\])?/g,
      (_match, routeIndex: string, field: string, itemIndex?: string) => {
        const route = `Route ${Number(routeIndex) + 1}`;
        const label = labels[field] || field;
        if (itemIndex === undefined) {
          return `${route} ${label}`;
        }
        return `${route} ${label}, entry ${Number(itemIndex) + 1}`;
      },
    )
    .replace(
      /(success_email|failure_email)\[(\d+)\]/g,
      (_match, field: string, itemIndex: string) => (
        `${labels[field] || field}, entry ${Number(itemIndex) + 1}`
      ),
    );
}

function vendorUpdateError(data: unknown): VendorUpdateError {
  const parsed = readApiError(data, 'Failed to update vendor');
  return new VendorUpdateError(friendlyRouteEmailMessage(parsed.message), parsed.errorCode);
}

export class TransferRetryError extends Error {
  constructor(
    message: string,
    readonly errorCode?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'TransferRetryError';
  }
}

function readTransferRetry(data: unknown): TransferRetryResponse {
  const raw = data && typeof data === 'object' ? data as Record<string, unknown> : {};
  if (raw.status !== 'started' || typeof raw.transfer_id !== 'string') {
    const parsed = readApiError(data, 'The retry could not be started.');
    throw new TransferRetryError(parsed.message, parsed.errorCode);
  }
  const maxAttempts = typeof raw.max_attempts === 'number' ? raw.max_attempts : Number(raw.max_attempts);
  return {
    status: 'started',
    transfer_id: raw.transfer_id,
    max_attempts: Number.isFinite(maxAttempts) ? maxAttempts : 0,
  };
}

export class VendorNotFoundError extends Error {
  constructor(
    readonly vendorId: string,
    readonly hostname: string
  ) {
    super('Vendor not found');
    this.name = 'VendorNotFoundError';
  }
}

function readHistory(data: unknown): TransferHistoryEntry[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter((entry): entry is TransferHistoryEntry => (
    !!entry && typeof entry === 'object'
  ));
}

export class TransferNotFoundError extends Error {
  constructor(readonly transferId: string) {
    super('Transfer not found');
    this.name = 'TransferNotFoundError';
  }
}

function normalizeMutation(data: unknown, fallbackId: string): VendorMutationResponse {
  const raw = (data && typeof data === 'object') ? data as Record<string, unknown> : {};
  const message = typeof raw.message === 'string'
    ? raw.message
    : typeof raw.error === 'string'
      ? raw.error
      : undefined;
  return {
    vendor_id: typeof raw.vendor_id === 'string' ? raw.vendor_id : fallbackId,
    status: typeof raw.status === 'string' ? raw.status : 'unhandled',
    message: message ? friendlyRouteEmailMessage(message) : undefined,
  };
}

function normalizeAdHocResponse(
  data: unknown,
  fallbackVendorId: string,
  fallbackPattern: string
): AdHocTransferResponse {
  const raw = (data && typeof data === 'object') ? data as Record<string, unknown> : {};
  const status = raw.status;
  const parsed = readApiError(data, '');
  return {
    status: status === 'accepted' || status === 'partial' || status === 'rejected'
      ? status
      : 'rejected',
    vendor_id: typeof raw.vendor_id === 'string' ? raw.vendor_id : fallbackVendorId,
    pattern: typeof raw.pattern === 'string' ? raw.pattern : fallbackPattern,
    triggered_by: typeof raw.triggered_by === 'string' ? raw.triggered_by : undefined,
    target_lambda_arn: typeof raw.target_lambda_arn === 'string'
      ? raw.target_lambda_arn
      : undefined,
    accepted: Array.isArray(raw.accepted) ? raw.accepted as AdHocAcceptedFile[] : [],
    rejected: Array.isArray(raw.rejected) ? raw.rejected as AdHocRejectedFile[] : [],
    error_code: parsed.errorCode,
    message: parsed.message || undefined,
  };
}

export const vendorApi = new VendorApiService();
